import type { ReactElement } from 'react'
import styled from 'styled-components'

import { businessLinks } from './business-links'
import { LinkCard } from '@/components/elements/LinkCard'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { SectionTitleBackground } from '@/components/elements/SectionTitleBackground'
import { ImageTitleLayout } from '@/components/layout'
import { Head } from '@/components/layouts/Head'

export const BusinessPage = () => {
  const anchorLinks = businessLinks.map((v) => {
    return { title: v.title, link: v.id, imgPath: v.anchorImgPath }
  })

  return (
    <>
      <Head
        title="事業内容"
        description="トライアルホールディングスのグループ企業の一覧、取り組む事業内容を掲載しています。"
        ogp={{ url: 'business/' }}
      />
      <BusinessMain>
        <LinkWrapper>
          <ul>
            {anchorLinks.map(({ title, link, imgPath }) => (
              <li key={title}>
                <LinkCard
                  title={title}
                  link={link}
                  imgPath={imgPath}
                  isScroll
                />
              </li>
            ))}
          </ul>
        </LinkWrapper>
        {businessLinks.map(
          ({
            id,
            title,
            engTitle,
            titleImgPath,
            businessContent,
            companies,
          }) => (
            <BusinessItem id={id} key={id}>
              <SectionTitleBackground
                bgColor="thin-blue"
                outerBgColor="white"
                padding={{ pc: '20px 200px 193px', sp: '0 0 68px' }}
              >
                <BusinessTitleWrapper>
                  <LogoWrapper>
                    <img
                      src={titleImgPath}
                      width={320}
                      height={184}
                      alt={`トライアルホールディングス${title}`}
                    />
                  </LogoWrapper>
                  <SectionTitle
                    titleText={engTitle}
                    subTitleText={title}
                    color="normal"
                    as="h2"
                  />
                  <Detail>{businessContent}</Detail>
                </BusinessTitleWrapper>
              </SectionTitleBackground>
              <BusinessLinks>
                {companies.map(({ name, ...rest }) => (
                  <LinkCard key={name} detail={name} {...rest} />
                ))}
              </BusinessLinks>
            </BusinessItem>
          )
        )}
      </BusinessMain>
    </>
  )
}

BusinessPage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '事業内容', path: '' },
  ]
  return (
    <ImageTitleLayout
      title="Our Business"
      subTitle="事業内容"
      imgUrl="/images/top-business.webp"
      breadCrumbs={breadCrumbs}
    >
      {page}
    </ImageTitleLayout>
  )
}

const BusinessMain = styled.div`
  overflow: hidden;
  margin: 98px auto 0;
  padding-bottom: 130px;

  @media screen and (max-width: 1047px) {
    margin: 40px auto 0;
    padding-bottom: 70px;
  }
`

const LinkWrapper = styled.div`
  width: 1040px;
  margin: 0 auto;

  ul {
    display: flex;
    column-gap: 46px;
  }

  @media screen and (max-width: 1047px) {
    width: 100%;
    padding: 0 20px;

    ul {
      flex-direction: column;
      align-items: center;
      row-gap: 37px;
    }
  }
`

const BusinessItem = styled.section`
  /*
    ヘッダーを固定しているため、ページ内アンカーの位置がそのままだとずれる。
    padding-topにヘッダーの高さを指定して、ヘッダーの高さのネガティブマージンを指定。
    その状態から110pxのマージンを追加するため、差分は5pxになる。
  */
  padding-top: 105px;
  margin-top: 5px;

  @media screen and (max-width: 1047px) {
    padding-top: 73px;
    margin-top: 7px;
  }
`

const BusinessTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: 1047px) {
    padding: 92px 40px 0;
  }
`

const LogoWrapper = styled.div`
  margin-bottom: 40px;
  z-index: 8;

  @media screen and (max-width: 1047px) {
    position: absolute;
    top: -20px;

    img {
      width: 160px;
      height: 92px;
    }
  }
`

const Detail = styled.div`
  margin-top: 48px;
  font-size: 16px;
  line-height: 30px;
  width: 1040px;

  @media screen and (max-width: 1047px) {
    margin-top: 30px;
    font-size: 15px;
    line-height: 25px;
    width: 100%;
  }
`

const BusinessLinks = styled.ul`
  display: flex;
  margin: 70px auto 0;
  flex-wrap: wrap;
  gap: 45px;
  width: 1040px;

  @media screen and (max-width: 1047px) {
    width: 100%;
    gap: 38px;
    padding: 0 20px;
    justify-content: center;
  }
`
